import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Header,
  Button,
  Icon,
  Card,
  CardRow,
  CardDivider,
} from "../components/ui";
import { useToast } from "../hooks/useToast";
import { useStore } from "../store";
import { createPayment, redirectToCheckout } from "../services/payment";
import {
  logScreenView,
  logTipSelected,
  logPaymentStarted,
} from "../services/analytics";

export function SelectTipScreen() {
  const navigate = useNavigate();
  const toast = useToast();

  // Use selectors for reactive state
  const receipt = useStore((state) => state.receipt);
  const selectedTip = useStore((state) => state.payment.tipOption);
  const customTipAmount = useStore((state) => state.payment.customTipAmount);
  const venueId = useStore((state) => state.getVenueId());
  const venue = useStore((state) => state.app.venue);
  const showNoTip = venue?.showNoTip ?? true;
  const showCustomTip = venue?.showCustomTip ?? true;
  const setTipOption = useStore((state) => state.setTipOption);
  const setCustomTipAmount = useStore((state) => state.setCustomTipAmount);
  const extendSession = useStore((state) => state.extendSession);
  // Get receipt data
  const ocrData =
    receipt.ocrResult.status === "success" ? receipt.ocrResult.data : null;

  // Compute values directly to ensure re-renders on state change
  const subtotal = ocrData?.subtotal ?? 0;
  const tax = ocrData?.tax ?? 0;
  const total = receipt.editedTotal ?? ocrData?.total ?? 0;
  const isEdited = receipt.editedTotal !== null;

  // Tip percentages: use server suggestions for OCR, venue config for manual/edited
  const venueTipPercentages = venue?.tipPercentages ?? [15, 18, 25];
  const tipPercentages = isEdited
    ? venueTipPercentages
    : (ocrData?.tipSuggestions ?? []).slice(0, 3).map((s) => s.percent);

  // Get server tip suggestions (only used when not edited)
  const serverTipSuggestions = (ocrData?.tipSuggestions ?? []).slice(0, 3);

  // Calculate tip amount based on selected option
  const tipAmount = (() => {
    if (selectedTip === "none") return 0;
    if (selectedTip === "custom") return customTipAmount;

    if (isEdited) {
      // Manual/edited: calculate on frontend
      return total * (selectedTip / 100);
    } else {
      // OCR: use pre-calculated amount from server
      const suggestion = serverTipSuggestions.find(
        (s) => s.percent === selectedTip,
      );
      return suggestion?.amount ?? 0;
    }
  })();

  const finalTotal = total + tipAmount;

  const defaultTip = tipPercentages[1] ?? tipPercentages[0];

  // Set default tip to second option (18%) on initial mount only.
  // Empty deps intentional: user's tip selection should persist if they navigate back and return.
  useEffect(() => {
    extendSession();
    logScreenView("select_tip");
    if (defaultTip !== undefined) {
      setTipOption(defaultTip);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset tip selection if venue config disallows the current choice
  useEffect(() => {
    if (!showCustomTip && selectedTip === "custom") {
      setTipOption(defaultTip ?? 18);
    }
    if (!showNoTip && selectedTip === "none") {
      setTipOption(defaultTip ?? 18);
    }
  }, [showCustomTip, showNoTip]); // eslint-disable-line react-hooks/exhaustive-deps

  const [isProcessing, setIsProcessing] = useState(false);
  const isSubmittingRef = useRef(false);

  const handleCustomAmountChange = (value: string) => {
    const cleanValue = value.replace(/[^0-9.]/g, "");
    setCustomTipAmount(parseFloat(cleanValue) || 0);
  };

  const handlePay = async () => {
    // Use ref for immediate blocking to prevent double-tap race condition
    if (isSubmittingRef.current || isProcessing) return;
    isSubmittingRef.current = true;

    // Guard against missing receipt data
    if (!ocrData && !isEdited) {
      toast.error("Receipt data not found. Please scan your receipt again.");
      isSubmittingRef.current = false;
      return;
    }

    setIsProcessing(true);

    // Log tip selection
    const tipOption =
      typeof selectedTip === "number" ? `${selectedTip}%` : selectedTip;
    const tipPercent = typeof selectedTip === "number" ? selectedTip : null;
    logTipSelected(tipOption, tipPercent, tipAmount);

    try {
      // Get receipt data for payment
      const receiptId = ocrData?.receiptId;
      const venueName = ocrData?.venue.name || "Unknown Venue";

      // Convert dollars to cents
      const amountCents = Math.round(total * 100);
      const tipCents = Math.round(tipAmount * 100);

      // Create payment via API
      const response = await createPayment({
        venueId: venueId!,
        amountCents,
        tipCents,
        receiptId: receiptId?.startsWith("manual-") ? undefined : receiptId,
        description: `Payment at ${venueName}`,
        source: isEdited ? "manual" : "receipt",
      });

      logPaymentStarted(
        finalTotal,
        tipAmount,
        venueId!,
        isEdited ? "manual" : "receipt",
      );

      // Redirect to Stripe Checkout
      try {
        redirectToCheckout(response.checkoutUrl);
      } catch {
        toast.error("Could not open payment page. Please try again.");
        setIsProcessing(false);
        isSubmittingRef.current = false;
      }
    } catch (err) {
      console.error("Payment failed:", err);
      const msg =
        err instanceof Error
          ? err.message
          : "Payment failed. Please try again.";
      toast.error(msg);
      setIsProcessing(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <div className="min-h-[100dvh] w-full bg-[var(--color-background)] flex flex-col">
      <Header title="Add Tip" showBack onBack={() => navigate("/confirm")} />

      {/* Main Content */}
      <main className="flex-1 flex flex-col px-6 py-6 overflow-y-auto">
        {/* Title */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-2">
            Add a tip for the staff
          </h2>
        </div>

        {/* Tip Options */}
        <div className="space-y-4 mb-6">
          {/* Percentage buttons */}
          <div className="grid grid-cols-3 gap-3">
            {tipPercentages.map((percent) => {
              const isSelected = selectedTip === percent;
              // Calculate display amount: use server value if not edited, otherwise calculate
              const displayAmount = isEdited
                ? total * (percent / 100)
                : (serverTipSuggestions.find((s) => s.percent === percent)
                    ?.amount ?? total * (percent / 100));
              return (
                <button
                  key={percent}
                  onClick={() => setTipOption(percent)}
                  className={`
                    relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all
                    ${
                      isSelected
                        ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white scale-[1.02] shadow-lg"
                        : "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:border-gray-300"
                    }
                  `}
                >
                  <span className="text-lg font-semibold">{percent}%</span>
                  <span
                    className={`text-xs ${isSelected ? "text-white/80" : "text-[var(--color-text-muted)]"}`}
                  >
                    ${displayAmount.toFixed(2)}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Custom & No Tip */}
          {(showCustomTip || showNoTip) && (
            <div
              className={`grid gap-3 ${showCustomTip && showNoTip ? "grid-cols-2" : "grid-cols-1"}`}
            >
              {showCustomTip &&
                (selectedTip === "custom" ? (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                      <span className="text-lg font-semibold text-white">
                        $
                      </span>
                    </div>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      autoFocus
                      value={
                        customTipAmount > 0 ? customTipAmount.toString() : ""
                      }
                      onChange={(e) => handleCustomAmountChange(e.target.value)}
                      onFocus={(e) => {
                        setTimeout(() => {
                          e.target.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }, 100);
                      }}
                      className="
                        block w-full pl-9 pr-4 py-4
                        text-lg font-semibold text-white text-center
                        bg-[var(--color-primary)] rounded-xl
                        border-2 border-[var(--color-primary)]
                        focus:outline-none
                        placeholder:text-white/50
                      "
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => setTipOption("custom")}
                    className="
                      flex items-center justify-center gap-2 p-4 rounded-xl border-2 font-medium transition-all
                      border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:border-gray-300
                    "
                  >
                    <Icon name="edit" size={20} />
                    Custom
                  </button>
                ))}
              {showNoTip && (
                <button
                  onClick={() => setTipOption("none")}
                  className={`
                    flex items-center justify-center p-4 rounded-xl border-2 font-medium transition-all
                    ${
                      selectedTip === "none"
                        ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                        : "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:border-gray-300"
                    }
                  `}
                >
                  No Tip
                </button>
              )}
            </div>
          )}
        </div>

        {/* Order Summary */}
        <Card className="mb-6">
          <div className="space-y-3">
            {isEdited ? (
              /* Simplified view when total was manually edited */
              <CardRow label="Bill Total" value={`$${total.toFixed(2)}`} />
            ) : (
              /* Full breakdown from OCR */
              <>
                <CardRow label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
                <CardRow label="Tax" value={`$${tax.toFixed(2)}`} />
                {ocrData?.tipIncluded && ocrData.tipAmount && (
                  <CardRow
                    label="Service Fee / Other"
                    value={`$${ocrData.tipAmount.toFixed(2)}`}
                  />
                )}
              </>
            )}
            <CardRow
              label={`Tip${typeof selectedTip === "number" ? ` (${selectedTip}%)` : ""}`}
              value={`$${tipAmount.toFixed(2)}`}
              highlight
            />
            <CardDivider />
            <div className="flex justify-between items-end">
              <span className="font-medium text-[var(--color-text)]">
                Total
              </span>
              <span className="text-2xl font-semibold text-[var(--color-text)]">
                ${finalTotal.toFixed(2)}
              </span>
            </div>
          </div>
        </Card>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Pay Button */}
        <div className="space-y-4">
          <Button onClick={handlePay} disabled={isProcessing}>
            {isProcessing ? (
              <>
                <span className="animate-spin inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                Processing...
              </>
            ) : (
              <>
                Pay ${finalTotal.toFixed(2)}
                <Icon name="arrow-right" size={20} />
              </>
            )}
          </Button>

          {/* Secure Payment Badge */}
          <div className="flex items-center justify-center gap-2 text-xs text-[var(--color-text-light)]">
            <Icon name="lock" size={16} />
            <span>Secure payment via Stripe</span>
          </div>
        </div>
      </main>
    </div>
  );
}
